import { useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  List, ListItem, ListItemText, Alert, CircularProgress, Box,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { parseVCard, parseCSV } from '../../utils/contact-import';
import { importContacts } from '../../services/contacts';

interface ParsedContact {
  firstName?: string;
  lastName?: string;
  nicknames?: string[];
  email?: string;
  phone?: string;
}

interface ContactImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

export default function ContactImportDialog({ open, onClose, onImported }: ContactImportDialogProps) {
  const [parsed, setParsed] = useState<ParsedContact[]>([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);
  const [error, setError] = useState('');

  const handleFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    setError('');

    const text = await file.text();
    const name = file.name.toLowerCase();

    try {
      if (name.endsWith('.vcf') || name.endsWith('.vcard')) {
        setParsed(parseVCard(text));
      } else if (name.endsWith('.csv')) {
        setParsed(parseCSV(text));
      } else {
        setError('Unsupported file type. Use .vcf or .csv files.');
        setParsed([]);
      }
    } catch {
      setError('Failed to parse file.');
      setParsed([]);
    }
  }, []);

  const handleImport = async () => {
    setImporting(true);
    setError('');
    try {
      const res = await importContacts(parsed);
      setResult(res);
      onImported();
    } catch {
      setError('Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setParsed([]);
    setFileName('');
    setResult(null);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Contacts</DialogTitle>
      <DialogContent>
        <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} sx={{ mb: 2 }}>
          {fileName || 'Choose File (.vcf or .csv)'}
          <input type="file" hidden accept=".vcf,.vcard,.csv" onChange={handleFile} />
        </Button>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {result && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {result.imported} imported, {result.skipped} skipped (duplicate email).
          </Alert>
        )}

        {parsed.length > 0 && !result && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {parsed.length} contact{parsed.length !== 1 ? 's' : ''} found:
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              <List dense disablePadding>
                {parsed.map((c, i) => (
                  <ListItem key={i}>
                    <ListItemText
                      primary={[c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Unknown'}
                      secondary={c.email || undefined}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        {parsed.length > 0 && !result && (
          <Button onClick={handleImport} variant="contained" disabled={importing}>
            {importing ? <CircularProgress size={20} /> : `Import ${parsed.length}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
